import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatCents } from "@/lib/format";
import {
  ROTULO_BLOQUEIO,
  ROTULO_MOTIVO_PENDENCIA,
  ROTULO_TIPO_PLANILHA,
  type BloqueioIgnorado,
  type Pendencia,
  type PendenciaIgnorada,
  type ResultadoResolucao,
} from "@/data/folha";
import type { User } from "@/data/mock";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { AvisoContextual } from "@/components/folha/AvisoContextual";
import { IgnorarMotivoDialog } from "@/components/folha/IgnorarMotivoDialog";
import { CheckCircle2, EyeOff, Link2, Loader2, Sparkles, Undo2 } from "lucide-react";

interface Props {
  folhaId: string;
  pendencias: Pendencia[];
  /** Linhas de planilha ignoradas pelo RH. */
  ignoradas: PendenciaIgnorada[];
  /** Bloqueios do fechamento ignorados pelo RH (fora as linhas de planilha). */
  bloqueiosIgnorados: BloqueioIgnorado[];
  /** Folha em rascunho: dá para associar, ignorar e desfazer. */
  editavel: boolean;
  usuarios: User[];
  onResolvida: () => void;
}

export const PendenciasImportacao = ({
  folhaId,
  pendencias,
  ignoradas,
  bloqueiosIgnorados,
  editavel,
  usuarios,
  onResolvida,
}: Props) => {
  const [escolhas, setEscolhas] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState<string | null>(null);
  const [ignorando, setIgnorando] = useState<Pendencia | null>(null);
  const [desfazendo, setDesfazendo] = useState<string | null>(null);
  // Ligado por padrão: é justamente o que evita refazer isso todo mês.
  const [aprender, setAprender] = useState(true);

  const resolver = async (p: Pendencia) => {
    const colaboradorId = escolhas[p.id];
    if (!colaboradorId) {
      toast.error("Selecione o colaborador desta linha");
      return;
    }

    setSalvando(p.id);
    try {
      const r = await api.post<ResultadoResolucao>(
        `/folhas/${folhaId}/pendencias/${p.id}/resolver`,
        // Na Amil a quebra titular/dependente vem das colunas da planilha;
        // aqui o RH só diz de quem é a linha.
        { colaboradorId, salvarVinculo: aprender },
      );

      const extras = r.resolvidas - 1;
      toast.success(
        extras > 0
          ? `Linha associada, e mais ${extras} da mesma pessoa resolvida(s) junto`
          : `Linha ${p.linha} associada`,
        r.chavesAprendidas.length > 0
          ? { description: "Identificação guardada — nas próximas importações casa sozinho." }
          : undefined,
      );

      for (const recusada of r.chavesRecusadas) {
        toast.warning(`Não guardei "${recusada.valor}": ${recusada.motivo}`);
      }

      onResolvida();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao resolver pendência");
    } finally {
      setSalvando(null);
    }
  };

  const ignorar = async (motivo: string) => {
    if (!ignorando) return;
    await api.post(`/folhas/${folhaId}/pendencias/${ignorando.id}/ignorar`, { motivo });
    toast.success(`Linha ${ignorando.linha} ignorada`);
    onResolvida();
  };

  const desfazer = async (chave: string, caminho: string, metodo: "post" | "delete") => {
    setDesfazendo(chave);
    try {
      if (metodo === "post") await api.post(caminho, {});
      else await api.delete(caminho);
      toast.success("Voltou a ser pendência");
      onResolvida();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao desfazer");
    } finally {
      setDesfazendo(null);
    }
  };

  const ignorados = (
    <IgnoradosSecao
      ignoradas={ignoradas}
      bloqueiosIgnorados={bloqueiosIgnorados}
      editavel={editavel}
      desfazendo={desfazendo}
      onDesfazerLinha={(p) =>
        void desfazer(p.id, `/folhas/${folhaId}/pendencias/${p.id}/restaurar`, "post")
      }
      onDesfazerBloqueio={(indice) =>
        void desfazer(`b${indice}`, `/folhas/${folhaId}/bloqueios/ignorados/${indice}`, "delete")
      }
    />
  );

  if (pendencias.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex flex-col items-center gap-2 rounded-xl border bg-card py-12 text-center text-sm text-muted-foreground">
          <CheckCircle2 className="h-6 w-6 text-success" />
          Nenhuma pendência. Todas as linhas das planilhas foram associadas a um colaborador ou
          ignoradas.
        </div>
        {ignorados}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <AvisoContextual tom="alerta" icon={Link2}>
        <strong className="font-semibold">
          {pendencias.length} linha(s) de planilha não encontraram colaborador.
        </strong>{" "}
        Associe cada uma para que o valor entre na folha, ou corrija a planilha e importe de novo.
        Linha de quem já foi desligado pode ser <strong className="font-semibold">ignorada</strong>,
        com o motivo. A competência não pode ser fechada enquanto houver pendência.
      </AvisoContextual>
      <div className="flex items-start gap-2 rounded-xl border bg-card p-4">
        <Checkbox
          id="aprender-vinculo"
          checked={aprender}
          onCheckedChange={(v) => setAprender(v === true)}
          className="mt-0.5"
        />
        <Label htmlFor="aprender-vinculo" className="font-normal">
          Guardar a identificação da planilha no cadastro
          <span className="block text-xs text-muted-foreground">
            Nas próximas competências essas linhas casam sozinhas, e as outras linhas da mesma
            pessoa nesta importação já são resolvidas junto.
          </span>
        </Label>
      </div>
      <div className="overflow-hidden rounded-xl border bg-card">
        <Table className="min-w-[900px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-16 pl-5">Linha</TableHead>
              <TableHead>Origem</TableHead>
              <TableHead>Identificação na planilha</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="min-w-[240px]">Associar a</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendencias.map((p) => {
              const sugestoes = p.sugestoes ?? [];
              return (
                <TableRow key={p.id}>
                  <TableCell className="pl-5 text-muted-foreground">{p.linha}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">
                      {p.lote ? ROTULO_TIPO_PLANILHA[p.lote.tipo] : "—"}
                    </Badge>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      {ROTULO_MOTIVO_PENDENCIA[p.motivo]}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{p.chaveNome ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">
                      {[p.chaveEmail, p.chaveCpf, p.chaveCnpj].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {p.valor === null ? (
                      <span className="text-destructive">inválido</span>
                    ) : (
                      formatCents(p.valor)
                    )}
                    {/* A planilha já separa titular e dependentes; mostrar a
                        quebra deixa claro que ninguém precisa classificar à mão. */}
                    {p.valor !== null &&
                      (p.valorTitular !== null || p.valorDependente !== null) && (
                        <div className="mt-0.5 text-[10px] font-normal text-muted-foreground">
                          {formatCents(p.valorTitular ?? 0)} copart. titular ·{" "}
                          {formatCents(p.valorDependente ?? 0)} copart. dependentes ·{" "}
                          {formatCents(
                            p.valor - (p.valorTitular ?? 0) - (p.valorDependente ?? 0),
                          )}{" "}
                          mensalidade dependentes
                        </div>
                      )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1.5">
                      {sugestoes.length > 0 && !escolhas[p.id] && (
                        <div className="flex flex-wrap items-center gap-1">
                          <Sparkles className="h-3 w-3 text-primary" />
                          {sugestoes.map((s) => (
                            <Button
                              key={s.colaboradorId}
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-[11px]"
                              onClick={() =>
                                setEscolhas((prev) => ({ ...prev, [p.id]: s.colaboradorId }))
                              }
                            >
                              {s.nome}
                            </Button>
                          ))}
                        </div>
                      )}

                      <Select
                        value={escolhas[p.id] ?? ""}
                        onValueChange={(v) => setEscolhas((prev) => ({ ...prev, [p.id]: v }))}
                        disabled={p.valor === null}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Selecione o colaborador" />
                        </SelectTrigger>
                        <SelectContent>
                          {usuarios.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-stretch gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!editavel || p.valor === null || salvando === p.id}
                        onClick={() => void resolver(p)}
                      >
                        {salvando === p.id && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                        Associar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        disabled={!editavel || salvando === p.id}
                        onClick={() => setIgnorando(p)}
                      >
                        <EyeOff className="h-3.5 w-3.5" />
                        Ignorar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {ignorados}

      <IgnorarMotivoDialog
        open={!!ignorando}
        onClose={() => setIgnorando(null)}
        titulo={ignorando ? `Ignorar a linha ${ignorando.linha}` : "Ignorar linha"}
        descricao={
          ignorando && (
            <>
              <p>
                <strong className="text-foreground">{ignorando.chaveNome ?? "Linha sem nome"}</strong>
                {ignorando.valor !== null && <> — {formatCents(ignorando.valor)}</>}
                {ignorando.lote && <> ({ROTULO_TIPO_PLANILHA[ignorando.lote.tipo]})</>}
              </p>
              <p>
                O valor desta linha não entra na folha de ninguém. Use para quem já foi desligado e
                ainda veio na planilha.
              </p>
            </>
          )
        }
        onConfirmar={ignorar}
      />
    </div>
  );
};

/** Linhas e bloqueios ignorados, com o motivo — e o "Desfazer" enquanto a folha está aberta. */
const IgnoradosSecao = ({
  ignoradas,
  bloqueiosIgnorados,
  editavel,
  desfazendo,
  onDesfazerLinha,
  onDesfazerBloqueio,
}: {
  ignoradas: PendenciaIgnorada[];
  bloqueiosIgnorados: BloqueioIgnorado[];
  editavel: boolean;
  desfazendo: string | null;
  onDesfazerLinha: (p: PendenciaIgnorada) => void;
  onDesfazerBloqueio: (indice: number) => void;
}) => {
  if (ignoradas.length === 0 && bloqueiosIgnorados.length === 0) return null;

  const botaoDesfazer = (chave: string, onClick: () => void) =>
    editavel && (
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2 text-xs"
        disabled={desfazendo === chave}
        onClick={onClick}
      >
        {desfazendo === chave ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
        Desfazer
      </Button>
    );

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <div className="border-b px-5 py-3 text-sm font-semibold">
        Ignorados ({ignoradas.length + bloqueiosIgnorados.length})
      </div>
      <Table className="min-w-[760px]">
        <TableHeader>
          <TableRow>
            <TableHead className="pl-5">O que foi ignorado</TableHead>
            <TableHead>Motivo</TableHead>
            <TableHead>Por</TableHead>
            {editavel && <TableHead className="w-28" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {bloqueiosIgnorados.map((b, indice) => (
            <TableRow key={`b${indice}`}>
              <TableCell className="pl-5">
                <div className="font-medium">{ROTULO_BLOQUEIO[b.codigo] ?? b.codigo}</div>
                {b.nomes.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    {b.nomes.slice(0, 5).join(", ")}
                    {b.nomes.length > 5 && ` e mais ${b.nomes.length - 5}`}
                  </div>
                )}
              </TableCell>
              <TableCell className="max-w-[320px] whitespace-pre-wrap text-sm">{b.motivo}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {b.porNome ?? "—"}
                <div>{formatDateTime(b.em)}</div>
              </TableCell>
              {editavel && (
                <TableCell className="pr-4 text-right">
                  {botaoDesfazer(`b${indice}`, () => onDesfazerBloqueio(indice))}
                </TableCell>
              )}
            </TableRow>
          ))}
          {ignoradas.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="pl-5">
                <div className="font-medium">
                  Linha {p.linha} · {p.chaveNome ?? p.chaveEmail ?? "sem identificação"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {p.lote ? ROTULO_TIPO_PLANILHA[p.lote.tipo] : "Planilha"}
                  {p.valor !== null && <> · {formatCents(p.valor)}</>}
                </div>
              </TableCell>
              <TableCell className="max-w-[320px] whitespace-pre-wrap text-sm">{p.ignoradoMotivo}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {p.ignoradoPorNome ?? "—"}
                <div>{formatDateTime(p.resolvidoEm)}</div>
              </TableCell>
              {editavel && (
                <TableCell className="pr-4 text-right">
                  {botaoDesfazer(p.id, () => onDesfazerLinha(p))}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
